import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import InlineGroup from 'components/Common/InlineGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/pluralize'
import { convertMarkdownInclusionsToArray } from 'lib/markdownUtil'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { useFullInclusionTravelPeriodFilter } from 'hooks/useFullInclusionTravelPeriodFilter'
import AlwaysOnPriceDetails from 'components/OfferPage/AlwaysOn/AlwaysOnPriceDetails'
import { scrollToId } from 'lib/scrollUtils'

const MainSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;

  ${mediaQueryUp.lg} {
    min-width: ${rem(340)};
  }

  > * + * {
    margin-top: ${rem(8)};
  }
`

const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

const Wrapper = styled(InlineGroup)`
  width: 100%;
  min-width: 0;

  > * + * {
    margin-top: ${rem(6)};
  }

  flex-direction: column;
`

interface Props {
  offer: App.Offer;
  promotion: OfferPackageOptionView;
  checkIn: moment.Moment;
  rooms: Array<App.Occupants>;
}

export const DetailSegment = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  width: 100%;
  ${mediaQueryUp.md} {
    justify-content: space-between;
    display: grid;
    grid-template-columns: 60% 1fr;
    min-height: ${rem(260)};
  }
`

const LeftDetailSegment = styled.div`
  align-self: flex-start;

  > * + * {
    margin-top: ${rem(16)}
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};
  ${mediaQueryUp.md} {
    margin-left: ${rem(20)};
  }
`

const StyledGroup = styled(Group)`
  > * + * {
    margin-left: ${rem(8)};
  }
`

const BuyButton = styled(TextButton)`
  margin-top: ${rem(16)};
`

function AlwaysOnOfferPromotion(props: Props) {
  const {
    offer,
    promotion,
    checkIn,
    rooms,
  } = props

  const pkg = promotion.package
  const inclusions: App.TileInclusion[] = []
  const bonusInclusions = useFullInclusionTravelPeriodFilter(pkg.bonusInclusions ?? [], checkIn, checkIn?.clone().add(pkg.duration, 'days'))

  bonusInclusions.map((bonusInclusion) => {
    if ((bonusInclusion?.descriptionV2List?.length ?? 0) > 0) {
      return bonusInclusion.descriptionV2List?.map((inclusionV2) => {
        inclusions.push({
          text: inclusionV2.text,
          isHighlighted: inclusionV2.isHighlighted,
          icon: inclusionV2?.icon,
        })
      })
    } else {
      const description = bonusInclusion?.description?.startsWith('-') ?
        bonusInclusion?.description?.slice(1).trim() :
        bonusInclusion.description

      const descriptionLines = description ? convertMarkdownInclusionsToArray(description) : []
      descriptionLines.map((line) => {
        inclusions.push({
          text: line,
          isHighlighted: false,
          icon: bonusInclusion?.categoryIcon || '',
        })
      })
    }
  })

  const travelFrom = promotion?.schedules ? promotion?.schedules[0]?.travelPeriod.from : undefined
  const travelTo = promotion?.schedules ? promotion?.schedules[0]?.travelPeriod.to : undefined

  return (
    <Wrapper horizontal="start">
      <MainSection>
        <DetailSegment>
          <LeftDetailSegment>
            <StyledGroup direction="horizontal" verticalAlign="center" horizontalAlign="start">
              <ProductTypeLabel productType="limited_time_special"/>
            </StyledGroup>
            <VerticalSpacer gap={4}>
              <Heading variant="heading5">{pkg.name}</Heading>
              <TAOCancellationPolicies
                offerId={offer.id}
                useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies}
                timezoneOffset={offer.property?.timezoneOffset}
                uniqueKey={pkg.uniqueKey}
                checkInDate={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
                checkOutDate={checkIn ? checkIn.clone().add(pkg.duration, 'days').format(ISO_DATE_FORMAT) : undefined}
                occupants={rooms}
                cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
              />
            </VerticalSpacer>
            <TileInclusions
              offerTitle={offer.property?.name ? offer.property.name : offer.name}
              heading="Bonus inclusions for select packages"
              tileInclusionsList={inclusions}
              maxInclusionsCount={3}
            />
            <TravelPeriodSection>
              <BodyText variant="small" weight="normal" colour="neutral-two">
                {travelFrom && travelTo && (
                  <>
                    Travel between: {moment(travelFrom).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(travelTo).format(DMY_CASUAL_SHORT_FORMAT)}.
                    {' '}
                  </>
                )}
                Minimum stay: {pluralizeToString('night', pkg.duration)}
              </BodyText>
            </TravelPeriodSection>
          </LeftDetailSegment>
          <RightDetailSegment>
            <AlwaysOnPriceDetails
              rooms={rooms}
              checkIn={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
              packageView={promotion}
              offer={offer}
              canBuyNow={false}
            />
            <BuyButton
              kind="primary"
              data-filter="checkin"
              onClick={() => scrollToId(pkg?.roomType?.id ?? '')}
              fit="full-width"
            >
              Check availability
            </BuyButton>
          </RightDetailSegment>
        </DetailSegment>
      </MainSection>
    </Wrapper>
  )
}

export default AlwaysOnOfferPromotion
